import { useEffect, useRef } from "react";

/**
 * Circular doctor avatar with concentric audio-reactive rings.
 */
export default function AvatarOrb({
  imageSrc,
  physicianName,
  isUserSpeaking,
  isAssistantSpeaking,
  status,
  micAnalyserRef,
  remoteAnalyserRef,
  caption,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const size = canvas.clientWidth;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const getLevel = (analyser) => {
      if (!analyser) return 0;
      const data = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i];
      return sum / (data.length * 255);
    };

    const draw = (t) => {
      const size = canvas.clientWidth;
      const cx = size / 2;
      const cy = size / 2;
      ctx.clearRect(0, 0, size, size);

      let level = 0.08;
      if (isAssistantSpeaking) {
        level = Math.max(0.12, getLevel(remoteAnalyserRef?.current) * 1.8);
      } else if (isUserSpeaking) {
        level = Math.max(0.1, getLevel(micAnalyserRef?.current) * 1.6);
      } else {
        level = 0.08 + Math.sin(t / 900) * 0.025;
      }

      const rings = [
        { radius: size * 0.34, alpha: 0.55 },
        { radius: size * 0.42, alpha: 0.35 },
        { radius: size * 0.5, alpha: 0.2 },
      ];

      rings.forEach((ring, i) => {
        const r = ring.radius + level * size * (0.08 + i * 0.03);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(227, 0, 32, ${ring.alpha})`;
        ctx.lineWidth = 2 + level * 4;
        ctx.stroke();
      });

      // Soft outer glow
      const glow = ctx.createRadialGradient(cx, cy, size * 0.2, cx, cy, size * 0.5);
      glow.addColorStop(0, "rgba(227,0,32,0.08)");
      glow.addColorStop(1, "rgba(227,0,32,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.5, 0, Math.PI * 2);
      ctx.fill();

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    isAssistantSpeaking,
    isUserSpeaking,
    micAnalyserRef,
    remoteAnalyserRef,
  ]);

  const mode = isAssistantSpeaking
    ? "speaking"
    : isUserSpeaking
    ? "listening"
    : "idle";

  return (
    <div className="avatar-orb-wrap">
      {physicianName ? (
        <div className="avatar-physician">{physicianName}</div>
      ) : null}
      <div className={`avatar-orb ${mode}`}>
        <canvas ref={canvasRef} className="avatar-orb-canvas" />
        <img
          src={imageSrc}
          alt={physicianName || "Doctor avatar"}
          className="avatar-orb-image"
        />
      </div>
      <div className="avatar-caption">
        {status === "reconnecting"
          ? "Reconnecting…"
          : status === "connecting"
          ? "Connecting…"
          : caption ||
            (isUserSpeaking
              ? "Listening…"
              : isAssistantSpeaking
              ? "Speaking…"
              : "Ready")}
      </div>
    </div>
  );
}
